import { useState, type JSX } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { Modal, Table } from "react-bootstrap";
import { toast } from "react-toastify";
import { AxiosError } from "axios";

import { useAuthStore } from "src/store/authStore";
import NoDataFound from "src/components/common/NoDataFound";
import UserFormModal, {
  type UserFormPayload,
} from "src/components/modals/UserFormModal";
import ChangePasswordModal from "src/components/modals/ChangePasswordModal";
import {
  activateAdminUserApi,
  createAdminUserApi,
  deleteAdminUserApi,
  getAdminProgramsApi,
  getAdminUsersApi,
  updateAdminUserPasswordApi,
  updateAdminUserProfileApi,
  updateAdminUserProgramsApi,
  updateAdminUserRoleApi,
} from "src/api/userManagementApi";
import { ADMIN_ROLE_LABELS } from "src/types/userManagement";
import type { AdminUser } from "src/types/userManagement";

const TABLE_COLUMNS = [
  "Name",
  "Email",
  "Role",
  "Program",
  "Products",
  "Status",
];

const arraysHaveSameMembers = (a: number[], b: number[]): boolean => {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort((x, y) => x - y);
  const sortedB = [...b].sort((x, y) => x - y);
  return sortedA.every((value, index) => value === sortedB[index]);
};

const extractErrorMessage = (error: unknown, fallback: string): string => {
  const detail = (error as AxiosError<{ detail?: string }>)?.response?.data
    ?.detail;
  return detail || fallback;
};

export default function UserManagement(): JSX.Element {
  const { user: currentUser } = useAuthStore();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [deletingUser, setDeletingUser] = useState<AdminUser | null>(null);
  const [passwordUser, setPasswordUser] = useState<AdminUser | null>(null);

  const queryClient = useQueryClient();

  const { data: users, isLoading: isUsersLoading } = useQuery({
    queryKey: ["admin-users"],
    queryFn: getAdminUsersApi,
  });

  const { data: programs, isLoading: isProgramsLoading } = useQuery({
    queryKey: ["admin-programs"],
    queryFn: () => getAdminProgramsApi(),
  });

  // Patch a single row in the cached list instead of refetching every user —
  // each mutation below already knows (or receives from the API) the exact
  // new state of the one user it touched.
  const patchUserInCache = (userId: number, patch: Partial<AdminUser>) => {
    queryClient.setQueryData<AdminUser[]>(["admin-users"], (old) =>
      old?.map((u) => (u.id === userId ? { ...u, ...patch } : u)),
    );
  };

  const { mutate: createUser, isPending: isCreating } = useMutation({
    mutationFn: createAdminUserApi,
    onSuccess: (createdUser) => {
      queryClient.setQueryData<AdminUser[]>(["admin-users"], (old) =>
        old ? [...old, createdUser] : [createdUser],
      );
      toast.success("User created.");
      setIsFormOpen(false);
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, "Failed to create user."));
    },
  });

  const { mutate: saveEdits, isPending: isSavingEdits } = useMutation({
    mutationFn: async ({
      user,
      payload,
    }: {
      user: AdminUser;
      payload: UserFormPayload;
    }) => {
      const profileChanged =
        payload.firstName !== user.first_name ||
        payload.lastName !== user.last_name ||
        payload.email !== user.email;
      const roleChanged = payload.roleId !== user.role_id;
      const programsChanged = !arraysHaveSameMembers(
        payload.programIds,
        user.product_ids,
      );

      // Each endpoint echoes back the full, current user object, so chaining
      // these (only for the slices that actually changed) accumulates the
      // final state without ever needing to re-fetch the whole list.
      let updatedUser = user;
      if (profileChanged) {
        updatedUser = await updateAdminUserProfileApi(user.id, {
          first_name: payload.firstName,
          last_name: payload.lastName,
          email: payload.email,
        });
      }
      if (roleChanged) {
        updatedUser = await updateAdminUserRoleApi(user.id, payload.roleId);
      }
      if (programsChanged) {
        updatedUser = await updateAdminUserProgramsApi(
          user.id,
          payload.programIds,
        );
      }
      return updatedUser;
    },
    onSuccess: (updatedUser) => {
      patchUserInCache(updatedUser.id, updatedUser);
      toast.success("User updated.");
      setIsFormOpen(false);
      setEditingUser(null);
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, "Failed to update user."));
    },
  });

  const { mutate: deleteUser, isPending: isDeleting } = useMutation({
    mutationFn: (userId: number) => deleteAdminUserApi(userId),
    onSuccess: (_data, userId) => {
      patchUserInCache(userId, { active: false });
      toast.success("User deactivated.");
      setDeletingUser(null);
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, "Failed to deactivate user."));
    },
  });

  const { mutate: changePassword, isPending: isChangingPassword } =
    useMutation({
      mutationFn: ({ userId, password }: { userId: number; password: string }) =>
        updateAdminUserPasswordApi(userId, password),
      onSuccess: () => {
        toast.success("Password updated.");
        setPasswordUser(null);
      },
      onError: (error) => {
        toast.error(extractErrorMessage(error, "Failed to update password."));
      },
    });

  const {
    mutate: activateUser,
    isPending: isActivating,
    variables: activatingUserId,
  } = useMutation({
    mutationFn: (userId: number) => activateAdminUserApi(userId),
    onSuccess: (updatedUser) => {
      patchUserInCache(updatedUser.id, updatedUser);
      toast.success("User activated.");
    },
    onError: (error) => {
      toast.error(extractErrorMessage(error, "Failed to activate user."));
    },
  });

  const openCreateForm = () => {
    setEditingUser(null);
    setIsFormOpen(true);
  };

  const openEditForm = (user: AdminUser) => {
    setEditingUser(user);
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
    setEditingUser(null);
  };

  const handleSubmitForm = (payload: UserFormPayload) => {
    if (editingUser) {
      saveEdits({ user: editingUser, payload });
      return;
    }

    createUser({
      email: payload.email,
      password: payload.password,
      first_name: payload.firstName,
      last_name: payload.lastName,
      role_id: payload.roleId,
      product_ids: payload.programIds,
    });
  };

  const isSubmittingForm = editingUser ? isSavingEdits : isCreating;

  // Manager-role users don't have access to this page — redirect them out.
  // The nav link is already hidden for non-admins; this guards direct navigation.
  // Placed after all hooks above so hook call order stays stable across renders.
  if (currentUser && !currentUser.admin) {
    return <Navigate to="/dashboards" replace />;
  }

  return (
    <div className="container-fluid px-4 px-lg-5 py-3 overflow-y-auto overflow-x-hidden">
      <div className="row my-3 align-items-center">
        <div className="col-md-6 col-12">
          <h3 className="me-2 mb-0 fw-bold text-primary">User Management</h3>
        </div>
        <div className="col-md-6 col-12">
          <div className="d-flex justify-content-end">
            <button
              type="button"
              className="btn btn-primary"
              onClick={openCreateForm}
            >
              <i className="bi bi-plus-lg me-1" />
              Create User
            </button>
          </div>
        </div>
      </div>

      <div className="card shadow-sm p-3">
        <Table striped hover responsive>
          <thead>
            <tr className="text-center">
              {TABLE_COLUMNS.map((column) => (
                <th key={column} style={{ whiteSpace: "nowrap" }}>
                  {column}
                </th>
              ))}
              <th style={{ whiteSpace: "nowrap" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isUsersLoading ? (
              [...Array(5).keys()].map((i) => (
                <tr key={i}>
                  {[...TABLE_COLUMNS, "Actions"].map((column) => (
                    <td key={column} className="placeholder-glow">
                      <span className="placeholder placeholder-sm w-100" />
                    </td>
                  ))}
                </tr>
              ))
            ) : users && users.length > 0 ? (
              users.map((user) => (
                <tr key={user.id}>
                  <td className="text-center">
                    {`${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() ||
                      user.name}
                  </td>
                  <td className="text-center">{user.email}</td>
                  <td className="text-center">
                    {ADMIN_ROLE_LABELS[user.role_id] ?? user.role_id}
                  </td>
                  <td className="text-center">{user.program_name ?? "—"}</td>
                  <td className="text-center">
                    {user.product_names.length
                      ? user.product_names.join(", ")
                      : "—"}
                  </td>
                  <td className="text-center">
                    <span
                      className={`badge ${user.active ? "text-bg-success" : "text-bg-secondary"}`}
                    >
                      {user.active ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="text-center">
                    <div className="d-flex gap-2 justify-content-center">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => openEditForm(user)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => setPasswordUser(user)}
                        title="Change password"
                        aria-label={`Change password for ${
                          `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() ||
                          user.name
                        }`}
                      >
                        <i className="bi bi-key" aria-hidden="true" />
                      </button>
                      {user.active ? (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => setDeletingUser(user)}
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-success"
                          onClick={() => activateUser(user.id)}
                          disabled={
                            isActivating && activatingUserId === user.id
                          }
                        >
                          {isActivating && activatingUserId === user.id
                            ? "Activating..."
                            : "Activate"}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={TABLE_COLUMNS.length + 1}
                  className="bg-white border-0"
                >
                  <NoDataFound />
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      <UserFormModal
        show={isFormOpen}
        mode={editingUser ? "edit" : "create"}
        editingUser={editingUser}
        programs={programs ?? []}
        isProgramsLoading={isProgramsLoading}
        isSubmitting={isSubmittingForm}
        onClose={closeForm}
        onSubmit={handleSubmitForm}
      />

      <ChangePasswordModal
        user={passwordUser}
        isSubmitting={isChangingPassword}
        onClose={() => setPasswordUser(null)}
        onSubmit={(password) => {
          if (passwordUser) {
            changePassword({ userId: passwordUser.id, password });
          }
        }}
      />

      <Modal
        show={!!deletingUser}
        centered
        onHide={() => setDeletingUser(null)}
      >
        <Modal.Header closeButton={!isDeleting}>
          <Modal.Title>Deactivate User</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to deactivate{" "}
          <strong>
            {`${deletingUser?.first_name ?? ""} ${deletingUser?.last_name ?? ""}`.trim() ||
              deletingUser?.name}
          </strong>
          ? They will lose access until reactivated.
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-outline-secondary"
            onClick={() => setDeletingUser(null)}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => deletingUser && deleteUser(deletingUser.id)}
            disabled={isDeleting}
          >
            {isDeleting ? "Deactivating..." : "Deactivate"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
